import {
	GQLCategoryFragment,
	GQLGetProductsQueryVariables,
	GQLOrderDirection,
	GQLProductOrderByInput,
	GQLProductSideAvailableDecorationType,
	GQLProductStatusType,
	GQLProductTypeType,
	GQLQueryMode,
} from '@theprintbar/graphql';
import { booleanField, designParamsSchema, getSearchObject } from '@theprintbar/shared';
import { z } from 'zod';

export enum ProductQueryKeys {
	Page = 'page',
	Sort = 'sort',
	Search = 'search',
	Sameday = 'sameday',
	Type = 'type',
	Collection = 'collection',
	Category = 'category',
	Brand = 'brand',
	Price = 'price',
	Decoration = 'decoration',
	Color = 'color',
	Size = 'size',
	ItemsPerPage = 'itemsPerPage',
}

export const ProductFilterKeys = [
	ProductQueryKeys.Type,
	ProductQueryKeys.Collection,
	ProductQueryKeys.Category,
	ProductQueryKeys.Brand,
	ProductQueryKeys.Price,
	ProductQueryKeys.Decoration,
	ProductQueryKeys.Color,
	ProductQueryKeys.Size,
];

export enum ProductSortOptions {
	PriceAsc = 'priceAsc',
	PriceDesc = 'priceDesc',
	Popular = 'popular',
	Latest = 'latest',
}

export const productPageSize = 24;

export const productSearchSchema = z.preprocess(
	raw => {
		const obj = { ...(raw as Record<string, any>) };
		if (obj.brands !== undefined && obj.brand === undefined) {
			obj.brand = Array.isArray(obj.brands) ? obj.brands[0] : obj.brands;
			delete obj.brands;
		}
		return obj;
	},
	designParamsSchema.extend({
		[ProductQueryKeys.Page]: z.coerce.number().min(1).optional(),
		[ProductQueryKeys.Sort]: z.nativeEnum(ProductSortOptions).optional(),
		[ProductQueryKeys.Search]: z.coerce.string().optional(),
		[ProductQueryKeys.Sameday]: booleanField(),
		[ProductQueryKeys.Type]: z.nativeEnum(GQLProductTypeType).optional(),
		[ProductQueryKeys.Collection]: z.coerce.string().optional(),
		[ProductQueryKeys.Category]: z.array(z.string()).optional(),
		[ProductQueryKeys.Brand]: z.coerce.string().optional(),
		[ProductQueryKeys.Price]: z.array(z.coerce.number()).min(1).max(2).optional(),
		[ProductQueryKeys.Decoration]: z.nativeEnum(GQLProductSideAvailableDecorationType).optional(),
		[ProductQueryKeys.Color]: z.coerce.string().optional(),
		[ProductQueryKeys.Size]: z.coerce.string().optional(),
		[ProductQueryKeys.ItemsPerPage]: z.coerce.number().optional(),
	}),
);

export type ProductSearchQuery = z.infer<typeof productSearchSchema>;

interface ProductSearchVariablesProps {
	request: Request;
	isPod?: boolean;
	isWeb?: boolean;
	sameday?: boolean;
	category?: GQLCategoryFragment;
	includeDrafts?: boolean;
	orderBy?: GQLProductOrderByInput[];
}

export function getProductSearchVariables({
	request,
	isPod,
	isWeb,
	sameday,
	includeDrafts,
	category,
	orderBy,
}: ProductSearchVariablesProps) {
	const { error, data: params } = productSearchSchema.safeParse(getSearchObject(request));

	const itemsPerPage = params?.[ProductQueryKeys.ItemsPerPage] || productPageSize;

	if (sameday && params) {
		params[ProductQueryKeys.Sameday] = true;
	}

	if (error) {
		return {
			variables: {},
			params: {},
		};
	}

	let variables: Partial<GQLGetProductsQueryVariables> = {
		take: itemsPerPage,
		skip: params.page ? (params.page - 1) * itemsPerPage : 0,
		orderBy: orderBy ? orderBy : [{ popularity: GQLOrderDirection.Desc }],
		where: includeDrafts
			? {}
			: {
					status: { equals: GQLProductStatusType.Published },
			  },
	};

	if (category) {
		variables.where = {
			...variables.where,
			categories: {
				some: {
					slug: { equals: category.slug },
				},
			},
		};
	}

	if (isPod) {
		variables.where = {
			...variables.where,
			availablePOD: { equals: true },
		};
	}
	if (isWeb) {
		variables.where = {
			...variables.where,
			availableWeb: { equals: true },
		};
	}

	if (params[ProductQueryKeys.Sort]) {
		let orderByProduct: GQLProductOrderByInput = {};
		switch (params[ProductQueryKeys.Sort]) {
			case 'popular':
				orderByProduct = { popularity: GQLOrderDirection.Desc };
				break;
			case 'latest':
				orderByProduct = { createdAt: GQLOrderDirection.Desc };
				break;
			case 'priceAsc':
				orderByProduct = { salePrice: GQLOrderDirection.Asc };
				break;
			case 'priceDesc':
				orderByProduct = { salePrice: GQLOrderDirection.Desc };
				break;
		}

		variables.orderBy = {
			...orderByProduct,
		};
	}

	if (params[ProductQueryKeys.Sameday]) {
		variables.where = {
			...variables.where,
			isSameday: { equals: true },
		};
	}

	if (params[ProductQueryKeys.Decoration]) {
		switch (params[ProductQueryKeys.Decoration]) {
			case GQLProductSideAvailableDecorationType.Dtg:
				variables.where = {
					...variables.where,
					hasDTG: { equals: true },
				};
				break;
			case GQLProductSideAvailableDecorationType.Scr:
				variables.where = {
					...variables.where,
					hasSCR: { equals: true },
				};
				break;
			case GQLProductSideAvailableDecorationType.Emb:
				variables.where = {
					...variables.where,
					hasEMB: { equals: true },
				};
				break;
			case GQLProductSideAvailableDecorationType.Dtf:
				variables.where = {
					...variables.where,
					hasDTF: { equals: true },
				};
				break;
		}
	}

	if (params[ProductQueryKeys.Color]) {
		variables.where = {
			...variables.where,
			colours: {
				some: {
					colour: { groups: { some: { name: { equals: params[ProductQueryKeys.Color], mode: GQLQueryMode.Insensitive } } } },
				},
			},
		};
	}

	if (params[ProductQueryKeys.Size]) {
		variables.where = {
			...variables.where,
			variants: {
				some: {
					size: { code: { equals: params[ProductQueryKeys.Size], mode: GQLQueryMode.Insensitive } },
				},
			},
		};
	}

	if (params[ProductQueryKeys.Price]) {
		variables.where = {
			...variables.where,
			salePrice: {
				gte: params[ProductQueryKeys.Price][0].toString(),
				lte: params[ProductQueryKeys.Price][1]?.toString(),
			},
		};
	}

	// TODO: Switch Algolia in
	if (params[ProductQueryKeys.Search]) {
		const searchItems = params[ProductQueryKeys.Search]
			.split(' ')
			.map(item => item.trim())
			.filter(Boolean);
		variables.where = {
			...variables.where,
			AND: searchItems.map(term => ({
				OR: [
					{ name: { contains: term, mode: GQLQueryMode.Insensitive } },
					{ code: { contains: term, mode: GQLQueryMode.Insensitive } },
					{ brand: { name: { contains: term, mode: GQLQueryMode.Insensitive } } },
					{ categories: { some: { name: { contains: term, mode: GQLQueryMode.Insensitive } } } },
				],
			})),
		};
	}

	if (params[ProductQueryKeys.Type]) {
		variables.where = {
			...variables.where,
			type: { equals: params[ProductQueryKeys.Type] },
		};
	}

	if (params[ProductQueryKeys.Collection]) {
		variables.where = {
			...variables.where,
			collections: {
				some: {
					code: { equals: params[ProductQueryKeys.Collection], mode: GQLQueryMode.Insensitive },
				},
			},
		};
	}

	if (params[ProductQueryKeys.Category]?.length) {
		const [parent, child] = params[ProductQueryKeys.Category];
		variables.where = {
			...variables.where,
			categories: {
				some: !child
					? {
							OR: [
								{ code: { equals: parent, mode: GQLQueryMode.Insensitive } },
								{ parents: { some: { code: { equals: parent, mode: GQLQueryMode.Insensitive } } } },
							],
					  }
					: {
							code: { equals: child, mode: GQLQueryMode.Insensitive },
							parents: {
								some: {
									code: { equals: parent, mode: GQLQueryMode.Insensitive },
								},
							},
					  },
			},
		};
	}

	if (params[ProductQueryKeys.Brand]) {
		variables.where = {
			...variables.where,
			brand: {
				code: { equals: params[ProductQueryKeys.Brand], mode: GQLQueryMode.Insensitive },
			},
		};
	}

	return {
		variables,
		params,
	};
}

export function getProductSearchURL(param: ProductQueryKeys, value: any, currentQuery: ProductSearchQuery) {
	let params = new URLSearchParams();
	// Set params from the last query that have a non-default value
	Object.entries(currentQuery).map(([key, value]) => {
		params.set(key, typeof value !== 'string' ? JSON.stringify(value) : value);
	});

	// Reset the page if we're changing the search
	if (param !== ProductQueryKeys.Page) params.delete(ProductQueryKeys.Page);

	if (!value) {
		// Passing an empty value means we want to clear the key
		params.delete(param);
	} else if (param === ProductQueryKeys.Price) {
		params.set(param, JSON.stringify(value));
	} else if (param === ProductQueryKeys.Category) {
		const array = Array.isArray(value) ? Array.from<string>(value) : [value];
		const current = currentQuery[ProductQueryKeys.Category];
		// If there are existing category filters, e.g: category=['shirts', 'hoodies']
		if (!!current && !!current.length) {
			const [parent, child] = array;

			//If there's nothing in the array, remove the key
			if (!parent && !child) {
				params.delete(param);
				// If the parent isn't in the list just reset the key
			} else if (current[0] !== parent) {
				params.set(param, JSON.stringify(array));
				//The parent matches, and there's no child, so remove the key
			} else if (!child) {
				if (!!current[1]) {
					params.set(param, JSON.stringify(array));
				} else {
					// If the child is in the list, remove it
					params.delete(param);
				}
			} else {
				// If the child is in the list, remove it
				if (current[1] !== child) {
					params.set(param, JSON.stringify(array));
				} else {
					params.delete(param);
				}
			}
		} else {
			// Otherwise just set it
			params.set(param, JSON.stringify(array));
		}
	} else {
		switch (param) {
			default:
				// This works for all simple key=value pairs
				// If there's no process set, set it
				// If there is a process set, and it's not the same as the one we're setting, set it
				if (!currentQuery[param] || currentQuery[param] !== value) {
					params.set(param, value);
					// Otherwise remove it
				} else {
					params.delete(param);
				}
				break;
		}
	}

	return `?${decodeURIComponent(params.toString())}`;
}
